import { Component, ChangeDetectionStrategy, computed, inject, input, output, signal } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { CdkDrag, CdkDragDrop, CdkDragPreview, CdkDropList, DragDropModule } from '@angular/cdk/drag-drop';
import { OcrPrimeStore } from '../../../../store/ocr-prime.store';
import { DocumentTemplateWithPath } from '../../../../types/document-template';
import { AnchorTreeLeafNode } from '../../../../types/anchor-tree';
import { TemplateCardComponent } from '../template-card/template-card.component';
import { TemplateDragPreviewComponent } from '../template-drag-preview/template-drag-preview.component';
import { AnchorTreeCanvasComponent } from '../../../../components/anchor-tree-canvas/anchor-tree-canvas.component';
import { getTemplateAnchorStatus, TemplateAnchorStatus } from '../../../../utils/templates.utils';
import { collectLeaves } from '../../../../utils/anchor-tree.utils';

export interface AssignmentRequest {
  template: DocumentTemplateWithPath;
  targetLeafNodeId: string;
}

export const UNPLACED_DROP_LIST_ID = 'unplaced-templates';

/**
 * Read-only canvas rendering of the anchor tree with template cards projected
 * into its leaf nodes (Q16-A). The only editing action here is assignment:
 * leaves are CDK drop lists connected to each other and the unplaced list.
 * The canvas pan is locked while a card drag is active (CDK caches drop-list
 * rects at drag start, so the canvas must not move mid-drag).
 */
@Component({
  selector: 'app-template-tree-view',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatIconModule, MatTooltipModule, DragDropModule, CdkDrag, CdkDragPreview, CdkDropList,
    TemplateCardComponent, TemplateDragPreviewComponent, AnchorTreeCanvasComponent
  ],
  styles: [`
    :host { display: block; height: 100%; }
    .leaf-drop-zone {
      transition: background-color 150ms ease-out, outline-color 150ms ease-out;
      outline: 3px dashed transparent;
      outline-offset: -3px;
    }
    /* Every leaf can receive the card in flight… */
    .leaf-drop-zone.is-drop-candidate {
      outline-color: rgb(165 180 252 / 0.9);
      background-color: rgb(99 102 241 / 0.05);
    }
    /* …the one under the pointer is where the drop will land. Outlines live
       inside the zoomable canvas and thin out with it, so the target is
       carried mostly by fill — see the leaf-card tint below. */
    .leaf-drop-zone.is-drop-target {
      outline: 4px solid #6366f1;
      outline-offset: -4px;
      background-color: rgb(99 102 241 / 0.16);
    }
    :host ::ng-deep [data-testid^='canvas-leaf-']:has(.leaf-drop-zone.is-drop-target) {
      border-color: #6366f1;
      background-color: rgb(99 102 241 / 0.12);
    }
    /* A placeholder would add a card-sized cell to the fixed-width leaf and
       push the node over its neighbours mid-drag, so leaves signal the drop
       through their outline instead. ::ng-deep because the placeholder is a
       clone that may originate from the unplaced list. */
    :host ::ng-deep .cdk-drag-placeholder { display: none; }
    :host ::ng-deep .leaf-drop-zone .cdk-drag { cursor: grab; }
    :host ::ng-deep .leaf-drop-zone .cdk-drag:active { cursor: grabbing; }
    /* The card is also a stretched navigation link; keep the drag affordance. */
    :host ::ng-deep .leaf-drop-zone .cdk-drag a[aria-hidden='true'] { cursor: inherit; }
    @media (prefers-reduced-motion: reduce) {
      .leaf-drop-zone { transition: none; }
    }
  `],
  template: `
    @if (!treeRoot()) {
      <div class="flex flex-col items-center justify-center py-16 text-center" data-testid="tree-view-empty">
        <mat-icon class="!text-4xl !w-10 !h-10 text-zinc-300 dark:text-zinc-600 mb-3">account_tree</mat-icon>
        <h3 class="text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-1">No anchor tree yet</h3>
        <p class="text-xs text-zinc-500 dark:text-zinc-400 max-w-[300px]">
          Author the tree structure on the Anchor Tree page, then assign templates to its leaves here.
        </p>
      </div>
    } @else {
      <div class="h-full" data-testid="tree-view">
        <app-anchor-tree-canvas
          [root]="treeRoot()"
          [editable]="false"
          [dragActive]="dragActive()"
        >
          <ng-template #leafContent let-leaf>
            <div
              class="leaf-drop-zone grid grid-cols-[repeat(auto-fill,25rem)] content-start gap-3 min-h-[4.5rem] -mx-3 -mb-3 rounded-b-md px-3 pb-3 pt-1"
              [class.is-drop-candidate]="dragActive() && activeDropListId() !== leaf.id"
              [class.is-drop-target]="activeDropListId() === leaf.id"
              cdkDropList
              [id]="leaf.id"
              [cdkDropListData]="leaf.id"
              [cdkDropListConnectedTo]="connectedDropLists()"
              [cdkDropListSortingDisabled]="true"
              (cdkDropListEntered)="activeDropListId.set(leaf.id)"
              (cdkDropListExited)="onExited(leaf.id)"
              (cdkDropListDropped)="onDrop($event)"
            >
              @for (template of templatesForLeaf(leaf); track template.id) {
                <div
                  cdkDrag
                  [cdkDragData]="template"
                  [class.opacity-60]="assigningTemplateId() === template.id"
                  (pointerdown)="$event.stopPropagation()"
                  (cdkDragStarted)="cardDragStarted.emit()"
                  (cdkDragEnded)="onCardDragEnded()"
                >
                  <ng-template cdkDragPreview>
                    <app-template-drag-preview
                      [template]="template"
                      hint="Drop on another leaf to move it there"
                    />
                  </ng-template>
                  @if (assigningTemplateId() === template.id) {
                    <app-template-card
                      [template]="template"
                      [structure]="store.structuresMap()[template.fieldsStructureId]"
                      [anchorStatus]="statusFor(template)"
                      [isBusy]="true"
                    />
                  } @else {
                    <app-template-card
                      [template]="template"
                      [structure]="store.structuresMap()[template.fieldsStructureId]"
                      [anchorStatus]="statusFor(template)"
                      [isDeleting]="deletingTemplateId() === template.id"
                      [isBusy]="togglingTemplateId() === template.id"
                      (deleteClicked)="deleteClicked.emit($event)"
                      (exportClicked)="exportClicked.emit($event)"
                      (toggleBlockedClicked)="toggleBlockedClicked.emit($event)"
                      (reextractClicked)="reextractClicked.emit($event)"
                    />
                  }
                </div>
              }
              @for (missingId of missingTemplateIds(leaf); track missingId) {
                <div class="col-span-full flex items-center gap-1.5 rounded-lg border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 px-3 py-2 text-xs text-amber-700 dark:text-amber-300"
                     [attr.data-testid]="'tree-leaf-missing-template-' + missingId">
                  <mat-icon class="!text-sm !w-4 !h-4 !leading-4">help_outline</mat-icon>
                  Unknown template {{ missingId }} — deleted?
                </div>
              }
              @if (isEmpty(leaf)) {
                <div
                  class="col-span-full flex items-center justify-center gap-1.5 rounded-lg border border-dashed border-indigo-200 dark:border-indigo-800 py-4 text-[11px] font-medium text-indigo-400 dark:text-indigo-300"
                  [attr.data-testid]="'tree-leaf-empty-' + leaf.id"
                >
                  <mat-icon class="!text-sm !w-4 !h-4 !leading-4" aria-hidden="true">add</mat-icon>
                  Drop a template here
                </div>
              }
            </div>
          </ng-template>
        </app-anchor-tree-canvas>
      </div>
    }
  `
})
export class TemplateTreeViewComponent {
  readonly store = inject(OcrPrimeStore);

  /** Template id currently in the middle of an assignment flow (spinner). */
  assigningTemplateId = input<string | null>(null);
  /** Template id currently being deleted (red spinner overlay). */
  deletingTemplateId = input<string | null>(null);
  /** Template id whose blocked state is currently being toggled. */
  togglingTemplateId = input<string | null>(null);
  /** True while any template card on the page is being dragged. */
  dragActive = input(false);

  assignmentRequested = output<AssignmentRequest>();
  deleteClicked = output<DocumentTemplateWithPath>();
  exportClicked = output<DocumentTemplateWithPath>();
  toggleBlockedClicked = output<DocumentTemplateWithPath>();
  reextractClicked = output<DocumentTemplateWithPath>();
  cardDragStarted = output<void>();
  cardDragEnded = output<void>();

  readonly treeRoot = computed(() => this.store.anchorTreeRoot());

  /** Leaf the dragged card currently hovers, i.e. where a drop would land. */
  readonly activeDropListId = signal<string | null>(null);

  /** All leaf drop-list ids + the unplaced list, so drags connect everywhere. */
  readonly connectedDropLists = computed<string[]>(() => [
    ...collectLeaves(this.treeRoot()).map(l => l.id),
    UNPLACED_DROP_LIST_ID
  ]);

  onDrop(event: CdkDragDrop<string>): void {
    this.activeDropListId.set(null);
    const template = event.item.data as DocumentTemplateWithPath;
    const targetLeafNodeId = event.container.data;
    if (!template || !targetLeafNodeId) {
      return;
    }
    // Dropping back on the same leaf is a no-op.
    if (event.previousContainer === event.container) {
      return;
    }
    this.assignmentRequested.emit({ template, targetLeafNodeId });
  }

  onExited(leafId: string): void {
    if (this.activeDropListId() === leafId) {
      this.activeDropListId.set(null);
    }
  }

  onCardDragEnded(): void {
    this.activeDropListId.set(null);
    this.cardDragEnded.emit();
  }

  isEmpty(leaf: AnchorTreeLeafNode): boolean {
    return (leaf.templateIds ?? []).length === 0;
  }

  templatesForLeaf(leaf: AnchorTreeLeafNode): DocumentTemplateWithPath[] {
    const map = this.store.templatesMap();
    return (leaf.templateIds ?? [])
      .map(id => map[id])
      .filter((t): t is DocumentTemplateWithPath => !!t);
  }

  missingTemplateIds(leaf: AnchorTreeLeafNode): string[] {
    const map = this.store.templatesMap();
    return (leaf.templateIds ?? []).filter(id => !map[id]);
  }

  statusFor(template: DocumentTemplateWithPath): TemplateAnchorStatus {
    return getTemplateAnchorStatus(template, this.treeRoot(), this.store.fieldsByIdMap());
  }
}
